import FeatureGates from '@atlaskit/feature-gate-js-client';
import { usePostOfficeContext } from '@atlassian/post-office-context';
import { usePlacementFetchingContext } from '@atlassian/post-office-placement-data';
import { usePlacementContext } from '@post-office/placement-context';
import { createUsePlacement } from '@post-office/placement-contexts/hooks';
import type { ErrorState } from '@post-office/placement-contexts/hooks/types';
import { createUsePlacementWithClient } from '@post-office/placement-contexts/hooks/usePlacementWithClient';
import { type SerializableRecord } from '@post-office/serializable-record';
import React, { useMemo } from 'react';

import { type Config } from './types';

export const usePlacementApi = <T extends SerializableRecord>(path: string, config?: Config) => {
	const { placementId } = usePlacementContext();
	const context = usePostOfficeContext();
	const fetcher = usePlacementFetchingContext();

	const placementApi = React.useMemo(() => {
		if (!placementId) {
			return (): ErrorState => ({
				isLoading: false,
				data: undefined,
				error: new Error('No placementId found, make this is used as a child of PlacementProvider'),
			});
		}

		if (FeatureGates.checkGate('post-office-use-placement-with-client')) {
			return createUsePlacementWithClient(placementId, fetcher);
		}

		return createUsePlacement(placementId, fetcher);
	}, [placementId, fetcher]);

	const { queryParams = {} } = config ?? {};
	const placementParams = { ...context, ...queryParams };

	const apiDependencies = useMemo(() => {
		return [config?.autoRefetch?.updateKey];
	}, [config?.autoRefetch?.updateKey]);

	return placementApi<T, SerializableRecord>(path, placementParams, config?.requestOptions, {
		throttleTime: config?.throttleTime,
		isEnabled: config?.autoRefetch?.isEnabled,
		deps: apiDependencies,
	});
};
